import { useCallback } from 'react';
import { collection, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase'; // Adjust path
import { KanbanTask, TemplateType, AppAction, TaskScope, PermissionFlags, DataScope } from '@/types';
import { parseISO, differenceInDays } from 'date-fns'; // ADDED: date-fns imports

/**
 * 🛰️ UTILITY: Logic for calculating the gap between usage cycles.
 */
const calculateRecurrenceDelta = (lastDateIso: string | null | undefined): number => { // Added undefined
  if (!lastDateIso) return 0;
  const lastDate = parseISO(lastDateIso);
  const today = new Date();
  // Ensure dates are valid before calculating difference
  if (isNaN(lastDate.getTime()) || isNaN(today.getTime())) return 0;
  return Math.max(0, differenceInDays(today, lastDate));
};

/**
 * 🛰️ UTILITY: Rolling average calculator for replenishment prediction.
 */
const calculateAverageCycle = (deltas: number[]): number | undefined => { // Changed to undefined
  if (deltas.length === 0) return undefined; // Changed to undefined
  const sum = deltas.reduce((acc, val) => acc + val, 0);
  return Math.round(sum / deltas.length);
};

/**
 * 🧠 useConsumption Hook
 * Refactored for Hybrid Scoping: Silos predictive logic into Personal or Shared partitions.
 */
export const useConsumption = (
  userId: string,
  currentHouseholdId: string | null,
  dispatch: React.Dispatch<AppAction>,
  activeDataScope: DataScope,
  userPermissions?: PermissionFlags // ADDED: userPermissions
) => {

  const getTaskCollectionRef = useCallback((taskScope: TaskScope) => {
    // If it's a personal task OR no household is active, always use personalTasks
    if (taskScope === 'personal' || !currentHouseholdId) {
      return collection(db, 'users', userId, 'personalTasks');
    }
    // Otherwise, if household scope and household is active, use household tasks
    return collection(db, 'households', currentHouseholdId, 'tasks');
  }, [userId, currentHouseholdId]);

  /**
   * 📉 processConsumptionOnArchive
   * Handles predictive analysis while enforcing Privacy Boundaries.
   */
  const processConsumptionOnArchive = useCallback((
    task: KanbanTask,
    targetScope: TaskScope // Use TaskScope directly
  ): KanbanTask => {
    // 1. 🛡️ Safety Gate: Template check
    if (task.templateType !== TemplateType.GROCERY || !task.consumption) {
      return task;
    }

    // 🛡️ PERMISSION CHECK: Shared data adheres to the household's permission matrix
    // Only check if it's a household task and permissions are defined
    if (targetScope === 'household' && currentHouseholdId && userPermissions) {
      if (!userPermissions.canManageGroceries && !userPermissions.canDeleteGroceries) {
        console.warn("Permission Denied: User lacks permission to archive shared household items.");
        return task; // Return original task if unauthorized
      }
    }

    const effectiveScope: TaskScope = currentHouseholdId ? targetScope : 'personal';
    const todayISO = new Date().toISOString();

    // 2. Calculate delta (Time since this item was last archived)
    const lastDate = task.consumption.lastAddedHistory;
    const newDelta = calculateRecurrenceDelta(lastDate);

    // 3. Update the consumption history array
    const currentDeltas = task.consumption.dayBetweenRecurrence || [];
    const updatedDeltas = [...currentDeltas];

    if (lastDate && typeof newDelta === 'number' && newDelta > 0) {
      updatedDeltas.push(newDelta);
    }

    if (updatedDeltas.length > 20) { // Keep history manageable
      updatedDeltas.shift();
    }

    // 4. Construct updated consumption object
    const updatedConsumption = {
      ...task.consumption,
      lastAddedHistory: todayISO,
      dayBetweenRecurrence: updatedDeltas,
      averageCycle: calculateAverageCycle(updatedDeltas),
      remainingQuantity: 0, // Reset when archived to history
      stockLevel: 0
    };

    const updatedTask: KanbanTask = {
      ...task,
      status: 'history', // Ensure status is history
      scope: effectiveScope, // 🛰️ Explicitly label the data partition
      ownerId: effectiveScope === 'personal' ? userId : (currentHouseholdId as string), // Owner depends on scope
      consumption: updatedConsumption,
      localChangeTimestamp: Date.now(), // Client-side timestamp
      completedAt: todayISO, // Mark as completed when archived
      lastModifiedBy: userId,
    };

    // Dispatch an update to the global state immediately
    dispatch({ type: 'UPDATE_TASK', payload: { id: updatedTask.id, updates: updatedTask } });

    // Asynchronous Firestore update
    const taskRef = doc(getTaskCollectionRef(effectiveScope), updatedTask.id);
    updateDoc(taskRef, {
      consumption: updatedConsumption,
      completedAt: updatedTask.completedAt,
      localChangeTimestamp: serverTimestamp(), // Server-side timestamp for Firestore
      lastModifiedBy: updatedTask.lastModifiedBy,
      status: updatedTask.status,
      // Ensure ownerId is also updated if scope changed from household to personal
      ownerId: updatedTask.ownerId
    }).catch(e => console.error("Error updating consumption on archive:", e));

    return updatedTask; // Return the client-side updated task
  }, [userId, currentHouseholdId, userPermissions, dispatch, getTaskCollectionRef]);


  /**
   * 🛒 prepareTaskForRefill
   * Restores an item to 'todo' while maintaining its scoped partition logic.
   */
  const prepareTaskForRefill = useCallback((
    task: KanbanTask,
    targetScope: TaskScope
  ): KanbanTask => {
    const effectiveScope: TaskScope = currentHouseholdId ? targetScope : 'personal';

    const updatedConsumption = task.consumption
        ? {
            ...task.consumption,
            remainingQuantity: task.consumption.quantity || 1, // Reset to full quantity
            stockLevel: 1 // Full stock level
          }
        : undefined;

    const updatedTask: KanbanTask = {
      ...task,
      status: 'todo',
      scope: effectiveScope,
      ownerId: effectiveScope === 'personal' ? userId : (currentHouseholdId as string),
      localChangeTimestamp: Date.now(), // Client-side timestamp
      consumption: updatedConsumption,
      completedAt: undefined, // Clear completedAt as it's being refilled
      lastModifiedBy: userId,
    };

    // Dispatch an update to the global state immediately
    dispatch({ type: 'UPDATE_TASK', payload: { id: updatedTask.id, updates: updatedTask } });

    // Asynchronous Firestore update
    const taskRef = doc(getTaskCollectionRef(effectiveScope), updatedTask.id);
    updateDoc(taskRef, {
      status: updatedTask.status,
      scope: updatedTask.scope,
      ownerId: updatedTask.ownerId,
      localChangeTimestamp: serverTimestamp(),
      consumption: updatedConsumption,
      completedAt: updatedTask.completedAt,
      lastModifiedBy: updatedTask.lastModifiedBy
    }).catch(e => console.error("Error preparing task for refill:", e));

    return updatedTask;
  }, [userId, currentHouseholdId, dispatch, getTaskCollectionRef]);


  return {
    processConsumptionOnArchive,
    prepareTaskForRefill
  };
};